import type { IQueryPayload } from '@delasport/rule-engine-ui';
import type { IOption } from '@shared/form/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  FIELD_FILTERS_ALLOWED_MODULE,
  type IInitialFilter,
  type ISearchParameters,
  OPERATOR_SORT,
  OPERATOR_TYPE,
} from '@features/filters/types';
import { getFieldBasedPreviousFiltersInitialValues } from '@features/filters/utils/getFieldBasedFiltersInitialValues';
import {
  type BET_TRIGGER_ACTION_TYPE,
  type ICampaignBonusTemplateRow,
  TRIGGER_TYPE,
} from '@modules/automaticCampaigns/types';
import { hasPartialUpdates } from '@utils/hasPartialUpdates';

interface IInitialState {
  searchParameters: ISearchParameters;
  prevFilters: Record<string, IInitialFilter>;
  dialogState: {
    isConfirmBrandsDialogOpen: boolean;
  };
  activeAutomaticCampaign: {
    enabled: boolean;
    id: null | string;
    name: null | string;
  };
  campaignConfig: {
    activeTab: number;
    queryError: boolean;
    query?: IQueryPayload;
    trigger: TRIGGER_TYPE;
    currentBrands: IOption[];
    isGeneralErrors: boolean;
    previousBrands: IOption[];
    brandChangeReason: string;
    isRelatedCampaign: boolean;
    segmentQuery?: IQueryPayload;
    triggerQuery?: IQueryPayload;
    hasBrandsWithoutAccess: boolean;
    isClearRelatedCampaign: boolean;
    isFetchRelatedCampaign: boolean;
    isDirtyAdditionalConditions: boolean;
    isAppearanceAndContentErrors: boolean;
    actionType: null | BET_TRIGGER_ACTION_TYPE;
    bonusTemplates: ICampaignBonusTemplateRow[];
    isEmptyAccumulatedTriggerConditions: boolean;
  };
}

export const initialAutomaticCampaignSearchParameters = {
  page: 1,
  size: 10,
  filters: [{ value: true, field: 'enabled', type: OPERATOR_TYPE.EQUAL }],
  sorts: [
    {
      field: 'modified_at',
      order: OPERATOR_SORT.DESC,
    },
  ],
};

const initialState: IInitialState = {
  dialogState: {
    isConfirmBrandsDialogOpen: false,
  },
  searchParameters: initialAutomaticCampaignSearchParameters,
  activeAutomaticCampaign: {
    id: null,
    name: null,
    enabled: false,
  },
  prevFilters: getFieldBasedPreviousFiltersInitialValues(
    FIELD_FILTERS_ALLOWED_MODULE.AUTOMATIC_CAMPAIGN
  ),
  campaignConfig: {
    activeTab: 0,
    actionType: null,
    query: undefined,
    queryError: false,
    currentBrands: [],
    previousBrands: [],
    bonusTemplates: [],
    brandChangeReason: '',
    isGeneralErrors: false,
    segmentQuery: undefined,
    triggerQuery: undefined,
    isRelatedCampaign: false,
    trigger: TRIGGER_TYPE.DEPOSIT,
    isFetchRelatedCampaign: false,
    isClearRelatedCampaign: false,
    hasBrandsWithoutAccess: false,
    isDirtyAdditionalConditions: false,
    isAppearanceAndContentErrors: false,
    isEmptyAccumulatedTriggerConditions: false,
  },
};

const automaticCampaignsSlice = createSlice({
  initialState,
  name: 'automaticCampaignsSlice',
  reducers: {
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<Record<string, IInitialFilter>>
    ) {
      state.prevFilters = action.payload;
    },
    setActiveAutomaticCampaign(
      state: IInitialState,
      action: PayloadAction<IInitialState['activeAutomaticCampaign']>
    ) {
      state.activeAutomaticCampaign = action.payload;
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
    setCampaignDialogState(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['dialogState']>>
    ) {
      state.dialogState = { ...state.dialogState, ...action.payload };
    },
    setCampaignConfig(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['campaignConfig']>>
    ) {
      if (!hasPartialUpdates(state.campaignConfig, action.payload)) {
        return;
      }

      Object.assign(state.campaignConfig, action.payload);
    },
  },
});

export default automaticCampaignsSlice.reducer;

export const {
  setPrevFilters,
  setCampaignConfig,
  setSearchParameters,
  setCampaignDialogState,
  setActiveAutomaticCampaign,
} = automaticCampaignsSlice.actions;
